import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Spin } from 'antd';
import { CustomImage, CustomPageHeader, Text as CustomText, useText } from '@src/components';
import addCircle from '@src/assets/icons/add-circle.svg';
import trash from '@src/assets/icons/trash.svg';
import edit from '@src/assets/icons/edit-2.svg';
import trashHover from '@src/assets/icons/trash_hover.svg';
import editHover from '@src/assets/icons/edit_hover.svg';
import '@src/styles/demands/demands.scss';
import '@src/styles/costCenter/costCenter.scss';
import {
  createVehicleCostCenter,
  deleteVehicleCostCenter,
  getVehicleCostCenter,
  updateVehicleCostCenter,
} from '@src/store/slice/costCenterStore/costCenterSlice';
import CustomButtonHover from '@src/components/CustomButtonHover';
import CustomDCCDeleteModal from '@src/components/CustomDCCDeleteModal';
import CustomDCCEditModal from '@src/components/CustomDCCEditModal';

const CostCenter = (props) => {
  const dispatch = useDispatch();

  const { loading, vehicleCostCenters = [] } = useSelector((state) => state.costCenter);

  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);
  const [isEditHovered, setIsEditHovered] = useState(false);
  const [isTrashHovered, setIsTrashHovered] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isEditSuccessModalVisible, setIsEditSuccessModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isAddSuccessModalVisible, setIsAddSuccessModalVisible] = useState(false);
  const [editableDepartment, setEditableDepartment] = useState(null);

  const editCostCenterName = useText('editCostCenterName');
  const deleteCostCenter = useText('deleteCostCenter');
  const addNewCostCenter = useText('addNewCostCenter');
  const costCenterDeletedSuccessfully = useText('costCenterDeletedSuccessfully');
  const editCostCenterName2 = useText('editCostCenterName2');
  const newCostCenterAddedSuccessfully = useText('newCostCenterAddedSuccessfully');

  const fetchVehicleListData = async () => {
    let req = { vouId: 4 };
    if (isAdmin && selectedUserId) {
      req.customerId = parseInt(selectedUserId, 10);
    }
    await dispatch(getVehicleCostCenter(req));
  };

  useEffect(() => {
    fetchVehicleListData();
  }, [dispatch, selectedUserId]);

  const editItem = (item) => {
    setEditableDepartment(item);
    setIsEditModalVisible(true);
  };

  const deleteItem = async (item) => {
    setEditableDepartment(item);
    if (!item.vehicleCount) {
      const department = {
        customerId: isAdmin ? selectedUserId : 0,
        costCenterId: item.costCenterId,
      };
      const res = await dispatch(deleteVehicleCostCenter({ department }));
      if (res?.payload?.isSuccess) {
        setIsSuccessModalVisible(true);
        fetchVehicleListData();
      }
    } else {
      setIsModalVisible(true);
    }
  };

  const addItem = () => {
    setIsAddModalVisible(true);
  };

  const clickOk = async (item) => {
    if (!item?.length > 0) return;
    if (isAddModalVisible) {
      if (vehicleCostCenters.filter((cCenter) => cCenter.costCenterLabel === item).length > 0) {
        return;
      }
      setIsAddModalVisible(false);
      const department = {
        customerId: isAdmin ? selectedUserId : 0,
        costCenterLabel: item,
        vouId: 4,
      };
      const res = await dispatch(createVehicleCostCenter({ department }));
      if (res?.payload?.isSuccess) {
        setIsAddSuccessModalVisible(true);
        fetchVehicleListData();
      }
    } else if (editableDepartment) {
      if (
        vehicleCostCenters.filter(
          (cCenter) =>
            cCenter.costCenterLabel === item &&
            cCenter.costCenterId !== editableDepartment.costCenterId,
        ).length > 0
      ) {
        return;
      }
      setIsEditModalVisible(false);
      const department = {
        customerId: isAdmin ? selectedUserId : 0,
        costCenterLabel: item,
        costCenterId: editableDepartment.costCenterId,
      };
      const res = await dispatch(updateVehicleCostCenter({ department }));
      if (res?.payload?.isSuccess) {
        setIsEditSuccessModalVisible(true);
        fetchVehicleListData();
      }
      setEditableDepartment(null);
    }
  };

  const content = () => {
    return loading.vehicleCostCenters ? (
      <div className="loading-container" style={{ textAlign: 'center', padding: '250px' }}>
        <Spin size="large" className="custom-spin" />
      </div>
    ) : (
      <Card
        title=""
        className="balance-list-container"
        headStyle={{ fontFamily: 'UbuntuRegular', fontSize: '18px', fontWeight: 'bolder' }}
        extra={
          <button onClick={addItem} className="cost-center-button cost-center-button-text">
            <CustomImage src={addCircle} />
            <CustomText t="addCostCenter" />
          </button>
        }
      >
        <div className="cost-center-grid">
          {vehicleCostCenters?.map((item, index) => (
            <div className="cost-center-box" key={'vehicleCostCenters_' + item.costCenterId}>
              <span className="cost-center-box-title">{item.costCenterLabel}</span>
              <div className="cost-center-box-white">
                <span className="cost-center-box-white-title">{item.vehicleCount ?? 0}</span>
                <span className="cost-center-box-white-text"><CustomText t="numberOfVehicles" /></span>
              </div>

              <CustomButtonHover
                index={index}
                clickButton={() => editItem(item)}
                setIsHovered={setIsEditHovered}
              >
                <CustomImage src={isEditHovered === index ? editHover : edit} />
              </CustomButtonHover>

              <CustomButtonHover
                index={index}
                clickButton={() => deleteItem(item)}
                setIsHovered={setIsTrashHovered}
              >
                <CustomImage src={isTrashHovered === index ? trashHover : trash} />
              </CustomButtonHover>
            </div>
          ))}
        </div>
        <CustomDCCDeleteModal
          title={deleteCostCenter}
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          vehicleCount={editableDepartment?.vehicleCount}
          nameArea={editableDepartment?.costCenterLabel}
          isDepartment={false}
        />
        <CustomDCCDeleteModal
          title={deleteCostCenter}
          isModalVisible={isSuccessModalVisible}
          setIsModalVisible={setIsSuccessModalVisible}
          deleted={costCenterDeletedSuccessfully}
        />
        <CustomDCCDeleteModal
          title={editCostCenterName}
          isModalVisible={isEditSuccessModalVisible}
          setIsModalVisible={setIsEditSuccessModalVisible}
          deleted={editCostCenterName2}
        />
        <CustomDCCEditModal
          title={editCostCenterName}
          isModalVisible={isEditModalVisible}
          setIsModalVisible={setIsEditModalVisible}
          clickOk={clickOk}
          isCostCenter
          editItem={editableDepartment}
        />
        <CustomDCCEditModal
          title={addNewCostCenter}
          isModalVisible={isAddModalVisible}
          setIsModalVisible={setIsAddModalVisible}
          clickOk={clickOk}
          isCostCenter
        />
        <CustomDCCDeleteModal
          title={addNewCostCenter}
          isModalVisible={isAddSuccessModalVisible}
          setIsModalVisible={setIsAddSuccessModalVisible}
          deleted={newCostCenterAddedSuccessfully}
        />
      </Card>
    );
  };

  return (
    <CustomPageHeader
      data-testid="demands-test-id"
      title={isAdmin ? null : <CustomText t="costCenters" />}
      showBreadCrumb={!isAdmin}
      showHelpButton={!isAdmin}
      adminCustomHeaderContent={isAdmin ? 'custom-header-content-subpage' : ''}
      routes={['ticketCarHeader']}
    >
      {content()}
    </CustomPageHeader>
  );
};

export default CostCenter;
